import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { notificationsNav } from '@/components/px/nav'
import {
  dismissNotification,
  fetchNotifications,
  listRetried,
  markAllNotificationsRead,
  markNotificationRead
} from '@/features/notifications/notificationsSlice'
import {
  selectNotificationItems,
  selectNotificationsCache,
  selectNotificationsError
} from '@/features/notifications/notificationsSelectors'
import type { NotificationSeverity } from '@/features/notifications/types'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { formatRelativeTime } from '@/lib/format'
import { useAppDispatch, useAppSelector } from '@/store'

const SEVERITY_TONE: Record<NotificationSeverity, StatusTone> = {
  critical: 'blocked',
  high: 'blocked',
  warning: 'review',
  info: 'neutral'
}

const SEVERITIES: readonly NotificationSeverity[] = ['critical', 'high', 'warning', 'info']

/**
 * The notification centre (`/notifications`, PX-W6.2, AC-6): the persisted
 * rows with severity/status filters (server-side params), per-row mark-read
 * through the API, dismiss as the hard delete the px server defines, and
 * `actions[]` deep links rendering `contextHref` verbatim (canon).
 */
export default function NotificationsPage() {
  const { t } = useTranslation(['notifications', 'people'])
  const dispatch = useAppDispatch()
  const role = useAppSelector(selectActiveRoleKey)

  const items = useAppSelector(selectNotificationItems)
  const cache = useAppSelector(selectNotificationsCache)
  const error = useAppSelector(selectNotificationsError)

  const [severity, setSeverity] = useState('')
  const [status, setStatus] = useState('')

  useEffect(() => {
    void dispatch(fetchNotifications({ severity, status }))
  }, [severity, status, dispatch])

  const onSeverityChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setSeverity(event.target.value)
  }

  const onStatusChanged = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setStatus(event.target.value)
  }

  const onRetryClicked = (): void => {
    dispatch(listRetried())
    void dispatch(fetchNotifications({ severity, status }))
  }

  const onMarkReadClicked = (id: string): void => {
    void dispatch(markNotificationRead(id))
  }

  const onMarkAllClicked = (): void => {
    void dispatch(markAllNotificationsRead())
  }

  const onDismissClicked = (row: { id: string }): void => {
    if (!window.confirm(t('notifications:dismissConfirm'))) return
    void dispatch(dismissNotification(row.id))
  }

  return (
    <AppShell
      moduleLabel={t('people:title')}
      breadcrumb={<span>{t('notifications:title')}</span>}
      contextPill={t('people:chrome.contextPill')}
      nav={notificationsNav(role)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('notifications:title')}</h1>
        <p className="text-sm text-muted-foreground">{t('notifications:lede')}</p>
      </header>

      <section className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          {t('notifications:filters.severity')}
          <select
            value={severity}
            onChange={onSeverityChanged}
            className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
          >
            <option value="">{t('notifications:filters.severityAll')}</option>
            {SEVERITIES.map((value) => (
              <option key={value} value={value}>
                {t(`notifications:severity.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          {t('notifications:filters.status')}
          <select
            value={status}
            onChange={onStatusChanged}
            className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
          >
            <option value="">{t('notifications:filters.statusAll')}</option>
            <option value="unread">{t('notifications:status.unread')}</option>
            <option value="read">{t('notifications:status.read')}</option>
          </select>
        </label>
        <button
          type="button"
          onClick={onMarkAllClicked}
          className="ml-auto rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
        >
          {t('notifications:markAllRead')}
        </button>
      </section>

      <section className="mt-4 rounded-lg border border-border bg-card">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <div className="p-4">
            <ForbiddenPanel requested="/{orgId}/notifications" />
          </div>
        ) : error !== null ? (
          <div className="p-4">
            <ErrorPanel error={error} onRetryClicked={onRetryClicked} />
          </div>
        ) : items.length === 0 ? (
          <EmptyPanel body={t('notifications:empty')} />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((row) => {
              const severityValue = row.severity ?? 'info'
              const unread = row.status !== 'read' && row.status !== 'dismissed'
              return (
                <li key={row.id} className="px-4 py-3" data-testid={`notification-${row.id}`}>
                  <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5">
                    <StatusPill tone={SEVERITY_TONE[severityValue]}>
                      {t(`notifications:severity.${severityValue}`)}
                    </StatusPill>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">
                        {row.title ?? row.id}
                      </span>
                      {row.description !== undefined && row.description !== '' && (
                        <span className="mt-0.5 block text-sm text-ink-soft">
                          {row.description}
                        </span>
                      )}
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {row.source ?? ''} · {formatRelativeTime(row.createdAt) ?? ''}
                      </span>
                      {(row.actions ?? []).length > 0 && (
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                          <SectionLabel>{t('notifications:actionsLabel')}</SectionLabel>
                          {(row.actions ?? []).map((action, index) =>
                            action.href !== undefined && action.href !== '' ? (
                              <a
                                key={index}
                                href={action.href}
                                className="text-sm text-foreground underline underline-offset-2 hover:text-primary"
                              >
                                {action.label ?? action.href}
                              </a>
                            ) : null
                          )}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2">
                      {unread && (
                        <button
                          type="button"
                          onClick={() => onMarkReadClicked(row.id)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
                        >
                          {t('notifications:markRead')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onDismissClicked(row)}
                        className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted"
                      >
                        {t('notifications:dismiss')}
                      </button>
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </AppShell>
  )
}
